import { getDriverApprovalStatus } from './driverDocuments';

export const AUTH_TOKEN_KEY = 'authToken';
export const AUTH_USER_KEY = 'authUser';
export const AUTH_REFRESH_TOKEN_KEY = 'authRefreshToken';

export const getStoredAuthToken = () => sessionStorage.getItem(AUTH_TOKEN_KEY);
export const getStoredRefreshToken = () => sessionStorage.getItem(AUTH_REFRESH_TOKEN_KEY);

export const sanitizeDisplayText = (value, fallback = '') => {
  const text = String(value ?? fallback ?? '');

  return Array.from(text)
    .map((character) => {
      const code = character.charCodeAt(0);
      return code <= 31 || code === 127 ? ' ' : character;
    })
    .join('')
    .replace(/\s+/g, ' ')
    .trim()
    .slice(0, 500);
};

const isSafeTokenValue = (token) => (
  typeof token === 'string' &&
  token.length <= 4096 &&
  token.trim() === token &&
  !/[\r\n]/.test(token)
);

export const storeAuthToken = (token) => {
  if (isSafeTokenValue(token)) {
    sessionStorage.setItem(AUTH_TOKEN_KEY, token);
  }
};

export const storeRefreshToken = (refreshToken) => {
  if (isSafeTokenValue(refreshToken)) {
    sessionStorage.setItem(AUTH_REFRESH_TOKEN_KEY, refreshToken);
  }
};

export const removeStoredAuthToken = () => {
  sessionStorage.removeItem(AUTH_TOKEN_KEY);
};

export const removeStoredRefreshToken = () => {
  sessionStorage.removeItem(AUTH_REFRESH_TOKEN_KEY);
};

export const getStoredUser = () => {
  const user = sessionStorage.getItem(AUTH_USER_KEY);

  if (!user) {
    return null;
  }

  try {
    return JSON.parse(user);
  } catch {
    sessionStorage.removeItem(AUTH_USER_KEY);
    return null;
  }
};

export const storeUser = (user) => {
  if (user) {
    sessionStorage.setItem(AUTH_USER_KEY, JSON.stringify(user));
  }
};

export const removeStoredUser = () => {
  sessionStorage.removeItem(AUTH_USER_KEY);
};

export const clearStoredAuth = () => {
  removeStoredAuthToken();
  removeStoredRefreshToken();
  removeStoredUser();
};

export const extractPayload = (responseData) => {
  if (!responseData || typeof responseData !== 'object' || Array.isArray(responseData)) {
    return responseData;
  }

  let current = responseData;
  let payload = {};
  for (let depth = 0; current && depth < 10; depth += 1) {
    if (Array.isArray(current)) return current;
    payload = { ...payload, ...current };
    current = current.data || current.payload || current.result;
    if (typeof current !== 'object') break;
  }
  delete payload.data; delete payload.payload; delete payload.result;
  return payload;
};

export const API_ERROR_MESSAGES = {
  TOPUP_PENDING: 'The existing top-up is being recovered. Check payment status before paying again.',
  GATEWAY_ORDER_REJECTED: 'Razorpay could not create the payment order. No payment was started. Please retry or contact payment support.',
  PREPAID_TOPUPS_DISABLED: 'Wallet top-ups are currently disabled. Contact support.',
  PAYMENT_PROVIDER_UNAVAILABLE: 'The payment provider is not configured. Contact support.',
  TOPUP_UNCERTAIN: 'Payment may still be processing. Check payment status before paying again.',
  INSUFFICIENT_DRIVER_WALLET_BALANCE: 'You need at least \u20b9500 in your wallet to accept orders.',
  GOOGLE_AUTH_REQUIRED: 'This account uses Google sign-in. Continue with Google or sign in using mobile OTP.',
  UNAUTHORIZED: 'Please login again.',
  FORBIDDEN: 'You do not have permission.',
  VALIDATION_FAILED: 'Please check the entered details.',
  RATE_LIMITED: 'Too many attempts. Please try again later.',
  INVALID_OTP: 'Invalid OTP.',
  OTP_EXPIRED: 'OTP expired. Request a new one.',
  OTP_ATTEMPTS_EXCEEDED: 'Too many wrong OTP attempts.',
  PAYMENT_ORDER_FAILED: 'Could not create payment order.',
  PAYMENT_VERIFICATION_FAILED: 'Payment verification failed.',
  PAYMENT_REQUIRED: 'Payment is required.',
  PAYMENT_NOT_VERIFIED: 'Payment is not verified.',
  PAYMENT_ALREADY_CONSUMED: 'This payment was already used.',
  PAYMENT_AMOUNT_MISMATCH: 'Payment amount mismatch.',
  PINCODE_NOT_SERVICEABLE: 'Pickup location is not serviceable.',
  DELIVERY_CREATE_FAILED: 'Could not create delivery.',
  DOCUMENT_UPLOAD_FAILED: 'Could not upload document.',
  PHONE_NOT_VERIFIED: 'Please verify your mobile number before continuing.',
  EMAIL_NOT_VERIFIED: 'Please verify your email before continuing.',
};

export const getApiErrorCode = (error) => {
  const payload = extractPayload(error?.response?.data) || {};
  const code =
    payload.error?.code || payload.errorCode || payload.code || (typeof payload.error === 'string' ? payload.error : null) ||
    error?.response?.data?.error?.code ||
    error?.response?.data?.errorCode ||
    error?.response?.data?.data?.code ||
    error?.response?.data?.error ||
    error?.response?.data?.code ||
    error?.error ||
    null;

  return sanitizeDisplayText(code).toUpperCase();
};

export const getErrorMessage = (error, fallbackMessage, options = {}) => {
  if (['ECONNABORTED', 'ETIMEDOUT'].includes(error?.code)) {
    return 'The server took too long to respond. Please try again.';
  }
  if (error?.code === 'ERR_NETWORK') {
    return 'Unable to reach the server. Check your connection and try again.';
  }
  const code = getApiErrorCode(error);
  const ignoredCodes = new Set(options.ignoreMappedCodes || []);

  if (code && API_ERROR_MESSAGES[code] && !ignoredCodes.has(code)) {
    return API_ERROR_MESSAGES[code];
  }

  const responseData = error?.response?.data;
  const payload = extractPayload(responseData);
  const responseMessage =
    payload?.message ||
    payload?.errorMessage ||
    payload?.details ||
    payload?.description ||
    responseData?.message ||
    responseData?.errorMessage ||
    responseData?.details ||
    responseData?.description ||
    (typeof responseData === 'string' ? responseData : '');

  return sanitizeDisplayText(responseMessage || error?.message || fallbackMessage, fallbackMessage);
};

const normalizeTokenValue = (value) => {
  if (!value) {
    return null;
  }

  if (typeof value === 'string') {
    const token = value.replace(/^Bearer\s+/i, '').trim();
    return isSafeTokenValue(token) ? token : null;
  }

  if (typeof value === 'object') {
    return normalizeTokenValue(
      value.token ||
      value.accessToken ||
      value.access_token ||
      value.jwt ||
      value.jwtToken ||
      value.jwt_token
    );
  }

  return null;
};

export const normalizeAuthResponse = (responseData) => {
  const payload = extractPayload(responseData) || {};
  const rawUser =
    payload.user ||
    payload.account ||
    payload.profile ||
    payload.driver ||
    payload.customer ||
    payload.data?.user ||
    null;

  return {
    ...payload,
    token: normalizeTokenValue(
      payload.token ||
      payload.accessToken ||
      payload.access_token ||
      payload.authToken ||
      payload.auth_token ||
      payload.jwt ||
      payload.jwtToken ||
      payload.jwt_token ||
      payload.authentication ||
      payload.auth ||
      null
    ),
    refreshToken: normalizeTokenValue(
      payload.refreshToken ||
      payload.refresh_token ||
      payload.refresh ||
      null
    ),
    user: rawUser
      ? {
          ...rawUser,
          role: normalizeUserRole(rawUser),
        }
      : null,
  };
};

export const normalizeUserRole = (user) => {
  if (!user || typeof user !== 'object') {
    return 'user';
  }

  const directRole =
    user.role ||
    user.userRole ||
    user.accountType ||
    user.userType ||
    user.type ||
    null;

  const authorityRole = Array.isArray(user.roles)
    ? user.roles[0]
    : Array.isArray(user.authorities)
      ? user.authorities[0]?.authority || user.authorities[0]
      : null;

  const driverRole = [directRole, ...(user.roles || []), ...(user.authorities || []).map((item) => item?.authority || item)]
    .some((item) => /^driver$/i.test(String(item || '').trim().replace(/^ROLE_/i, '')));
  const normalizedRole = String(directRole || (driverRole ? 'driver' : authorityRole || 'user'))
    .replace(/^ROLE_/i, '')
    .trim()
    .toLowerCase();

  if (normalizedRole === 'customer') {
    return 'user';
  }

  if (normalizedRole === 'administrator') {
    return 'admin';
  }

  return normalizedRole || 'user';
};

// Available roles and the role selected for this session are separate. A dual-role
// account must remain in the portal it signed into, including after /auth/me.
export const resolveSessionProfile = (profile, selectedRole) => {
  if (!selectedRole) return { ...profile, role: normalizeUserRole(profile) };
  const role = normalizeUserRole({ role: selectedRole });
  const roles = [profile.role, ...(profile.roles || []), ...(profile.authorities || []).map(item => item?.authority || item)]
    .filter(Boolean).map(value => normalizeUserRole({ role: value }));
  const kind = role === 'user' ? 'customer' : role;
  if ((!roles.includes(role) && profile[kind + 'Enabled'] !== true) || profile[kind + 'Active'] === false) {
    throw new Error('This account does not have active ' + role.toUpperCase() + ' access. Use the appropriate sign-in page.');
  }
  return { ...profile, role };
};

export const isDriverApproved = (user) => {
  if (!user || normalizeUserRole(user) !== 'driver') {
    return true;
  }

  return getDriverApprovalStatus(user) === 'APPROVED';
};

export const normalizeDelivery = (deliveryResponse) => {
  if (!deliveryResponse) {
    return deliveryResponse;
  }

  const payload = extractPayload(deliveryResponse) || {};
  const delivery =
    payload.delivery ||
    payload.order ||
    payload.deliveryDetails ||
    payload.orderDetails ||
    payload;

  return {
    ...delivery,
    id: delivery.id || delivery.deliveryId || delivery.delivery_id || delivery.orderId || delivery.order_id || delivery._id || '',
    status: typeof delivery.status === 'string'
      ? delivery.status.toUpperCase()
      : typeof delivery.deliveryStatus === 'string'
        ? delivery.deliveryStatus.toUpperCase()
        : delivery.status || delivery.deliveryStatus || 'PENDING',
    vehicleType: delivery.vehicleType || delivery.vehicle_type || delivery.vehicle || '',
    pickupLocation: normalizeLocation(
      delivery.pickupLocation || delivery.pickup_location,
      delivery.pickupName || delivery.pickup_name,
      delivery.pickupAddress || delivery.pickup_address,
      delivery.pickupLat ?? delivery.pickup_lat,
      delivery.pickupLng ?? delivery.pickup_lng
    ),
    dropLocation: normalizeLocation(
      delivery.dropLocation || delivery.drop_location,
      delivery.dropName || delivery.drop_name || delivery.deliveryName || delivery.delivery_name,
      delivery.dropAddress || delivery.drop_address || delivery.deliveryAddress || delivery.delivery_address,
      delivery.dropLat ?? delivery.drop_lat ?? delivery.deliveryLat ?? delivery.delivery_lat,
      delivery.dropLng ?? delivery.drop_lng ?? delivery.deliveryLng ?? delivery.delivery_lng
    ),
    estimatedCost: normalizeMoneyValue(
      delivery.estimatedCost,
      delivery.estimated_cost,
      delivery.actualCost,
      delivery.actual_cost,
      delivery.totalCost,
      delivery.total_cost,
      delivery.amount,
      delivery.price,
      delivery.fare
    ),
    actualCost: normalizeMoneyValue(
      delivery.actualCost,
      delivery.actual_cost,
      delivery.estimatedCost,
      delivery.estimated_cost,
      delivery.totalCost,
      delivery.total_cost,
      delivery.amount,
      delivery.price,
      delivery.fare
    ),
    driverVehicle:
      delivery.driverVehicle ||
      delivery.driver_vehicle ||
      delivery.vehicleNumber ||
      delivery.vehicle_number ||
      delivery.registrationNumber ||
      delivery.registration_number ||
      delivery.driver?.vehicleNumber ||
      delivery.driver?.vehicle_number ||
      delivery.driver?.registrationNumber ||
      delivery.driverDetails?.vehicleNumber ||
      delivery.assignedDriver?.vehicleNumber ||
      '',
  };
};

const normalizeLocation = (primaryLocation, name, address, lat, lng) => {
  if (primaryLocation && typeof primaryLocation === 'object') {
    return primaryLocation;
  }

  if (!name && !address && lat == null && lng == null) {
    return null;
  }

  return {
    name: name || address || '',
    address: address || name || '',
    lat: lat ?? null,
    lng: lng ?? null,
  };
};

const normalizeContact = (primaryContact, name, phone) => {
  if (primaryContact && typeof primaryContact === 'object') {
    return primaryContact;
  }

  if (!name && !phone) {
    return null;
  }

  return {
    name: name || '',
    phone: phone || '',
  };
};

const normalizeMoneyValue = (...values) => {
  const firstValue = values.find((value) => value !== undefined && value !== null && value !== '');
  const normalizedValue =
    typeof firstValue === 'string'
      ? Number(firstValue.replace(/[^\d.-]/g, ''))
      : Number(firstValue);

  return Number.isFinite(normalizedValue) ? normalizedValue : 0;
};

export const normalizeDriverOrder = (order) => {
  const payload = extractPayload(order);

  if (!payload) {
    return order;
  }

  const normalizedStatus =
    typeof payload.status === 'string'
      ? payload.status.toUpperCase()
      : typeof payload.deliveryStatus === 'string'
        ? payload.deliveryStatus.toUpperCase()
        : 'PENDING';

  return {
    ...payload,
    id: payload.id || payload.orderId || payload.order_id || payload.deliveryId || payload.delivery_id || payload._id || '',
    status: normalizedStatus,
    parcelSize: payload.parcelSize || payload.packageSize || payload.size || '',
    parcelWeight: Number(payload.parcelWeight ?? payload.packageWeight ?? payload.weight ?? 0),
    parcelDescription: payload.parcelDescription || payload.description || '',
    vehicleType: payload.vehicleType || payload.vehicle || '',
    estimatedCost: payload.estimatedCost == null || payload.estimatedCost === '' ? undefined : Number(payload.estimatedCost),
    pickupLocation: normalizeLocation(
      payload.pickupLocation,
      payload.pickupName || payload.pickup_name,
      payload.pickupAddress || payload.pickup_address,
      payload.pickupLat ?? payload.pickup_lat,
      payload.pickupLng ?? payload.pickup_lng
    ),
    dropLocation: normalizeLocation(
      payload.dropLocation,
      payload.dropName || payload.drop_name,
      payload.dropAddress || payload.drop_address,
      payload.dropLat ?? payload.drop_lat,
      payload.dropLng ?? payload.drop_lng
    ),
    senderContact: normalizeContact(
      payload.senderContact,
      payload.senderName || payload.sender_name,
      payload.senderPhone || payload.sender_phone
    ),
    receiverContact: normalizeContact(
      payload.receiverContact,
      payload.receiverName || payload.receiver_name,
      payload.receiverPhone || payload.receiver_phone
    ),
  };
};

export const normalizeDriverOrdersResponse = (responseData) => {
  const payload = extractPayload(responseData);
  const orders = Array.isArray(payload)
    ? payload
    : Array.isArray(payload?.content)
      ? payload.content
      : Array.isArray(payload?.items)
        ? payload.items
        : Array.isArray(payload?.orders)
          ? payload.orders
          : Array.isArray(payload?.data)
            ? payload.data
            : Array.isArray(payload?.data?.content)
              ? payload.data.content
              : Array.isArray(payload?.result)
                ? payload.result
                : [];

  return orders.map(normalizeDriverOrder);
};

export const normalizeDeliveryListResponse = (responseData) => {
  const payload = extractPayload(responseData);
  const deliveries = Array.isArray(payload)
    ? payload
    : Array.isArray(payload?.content)
      ? payload.content
      : Array.isArray(payload?.items)
        ? payload.items
        : [];

  return deliveries.map(normalizeDelivery);
};

export const normalizeTrackingResponse = (responseData) => {
  const payload = extractPayload(responseData) || {};
  const costPayload =
    payload.delivery ||
    payload.order ||
    payload.deliveryDetails ||
    payload.orderDetails ||
    payload;
  const driverPayload =
    payload.driver ||
    payload.driverDetails ||
    payload.assignedDriver ||
    payload.deliveryDriver ||
    {};

  return {
    ...payload,
    orderId: payload.orderId || payload.id || payload.deliveryId || '',
    estimatedCost: normalizeMoneyValue(
      payload.estimatedCost,
      payload.estimated_cost,
      payload.actualCost,
      payload.actual_cost,
      payload.totalCost,
      payload.total_cost,
      payload.amount,
      payload.price,
      payload.fare,
      costPayload.estimatedCost,
      costPayload.estimated_cost,
      costPayload.actualCost,
      costPayload.actual_cost,
      costPayload.totalCost,
      costPayload.total_cost,
      costPayload.amount,
      costPayload.price,
      costPayload.fare
    ),
    actualCost: normalizeMoneyValue(
      payload.actualCost,
      payload.actual_cost,
      payload.estimatedCost,
      payload.estimated_cost,
      payload.totalCost,
      payload.total_cost,
      payload.amount,
      payload.price,
      payload.fare,
      costPayload.actualCost,
      costPayload.actual_cost,
      costPayload.estimatedCost,
      costPayload.estimated_cost,
      costPayload.totalCost,
      costPayload.total_cost,
      costPayload.amount,
      costPayload.price,
      costPayload.fare
    ),
    driverName:
      payload.driverName ||
      payload.driver_name ||
      driverPayload.name ||
      [driverPayload.firstName || driverPayload.first_name, driverPayload.lastName || driverPayload.last_name]
        .filter(Boolean)
        .join(' ') ||
      '',
    driverPhone:
      payload.driverPhone ||
      payload.driver_phone ||
      driverPayload.phone ||
      driverPayload.mobile ||
      driverPayload.mobileNumber ||
      driverPayload.phoneNumber ||
      '',
    driverVehicle:
      payload.driverVehicle ||
      payload.driver_vehicle ||
      payload.vehicleNumber ||
      payload.vehicle_number ||
      driverPayload.vehicleNumber ||
      driverPayload.vehicle_number ||
      driverPayload.vehicleNo ||
      driverPayload.vehicle_no ||
      driverPayload.registrationNumber ||
      driverPayload.registration_number ||
      '',
    timeline: Array.isArray(payload.timeline) ? payload.timeline : [],
  };
};

export const normalizePaymentOrderResponse = (responseData) => {
  const payload = extractPayload(responseData) || {};

  return {
    ...payload,
    orderId: payload.orderId || payload.id || payload.razorpayOrderId || null,
    amount: payload.amount || 0,
    currency: payload.currency || 'INR',
    key: payload.key || payload.razorpayKey || null,
  };
};

export const normalizePaymentVerificationResponse = (responseData) => {
  const payload = extractPayload(responseData) || {};

  return {
    ...payload,
    status: payload.status ? String(payload.status).toUpperCase() : null,
    transactionId: payload.transactionId || payload.backendPaymentId || payload.id || null,
    amount: payload.amount || 0,
    currency: payload.currency || 'INR',
    paymentId: payload.paymentId || null,
  };
};

export const assertApiSuccess = (response, { delivery = false } = {}) => {
  let data = response?.data;
  for (let depth = 0; data && depth < 10; depth += 1) {
    const deliveryFailed = delivery === 'sms' ? data.smsSent === false : delivery === 'email' ? data.emailSent === false : delivery && data.emailSent === false && data.smsSent !== true;
    if (data.success === false || deliveryFailed) {
      const error = new Error(deliveryFailed ? (delivery === 'sms' ? 'SMS' : 'Email') + ' delivery failed. Please try again later.' : getErrorMessage({ response }, 'Request failed'));
      error.response = response;
      throw error;
    }
    data = data.data || data.payload || data.result;
  }
};
